/*
* Copyright 2026 Tecnalia, Basque Research & Technology Alliance (BRTA)
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*
*  SPDX-License-Identifier: Apache-2.0
*/

import { Component, computed, ElementRef, input, QueryList, signal, ViewChildren } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';

export type EdcJsonViewSectionKind = 'object-key' | 'array-item' | 'whole' | 'fallback' | 'empty';

export interface EdcJsonViewSection {
	trackId: string;
	title: string;
	formatted: string;
	kind: EdcJsonViewSectionKind;
	lineCount: number;
}

function lineCountOf(text: string): number {
	if (!text) return 0;
	return text.split('\n').length;
}

export function buildJsonViewSections(jsonText: string | null | undefined): EdcJsonViewSection[] {
	const text = (jsonText ?? '').trim();
	if (!text) {
		return [{ trackId: 'empty', title: '', formatted: '', kind: 'empty', lineCount: 0 }];
	}

	try {
		const parsed: unknown = JSON.parse(text);
		if (parsed !== null && typeof parsed === 'object') {
			if (Array.isArray(parsed)) {
				if (parsed.length === 0) {
					const formatted = '[]';
					return [{ trackId: 'root', title: '', formatted, kind: 'whole', lineCount: lineCountOf(formatted) }];
				}

				return parsed.map((item, i) => {
					const formatted = JSON.stringify(item, null, 2);
					return {
						trackId: `i-${i}`,
						title: `[${i}]`,
						formatted,
						kind: 'array-item' as const,
						lineCount: lineCountOf(formatted)
					};
				});
			}

			const keys = Object.keys(parsed as object);
			if (keys.length === 0) {
				const formatted = '{}';
				return [{ trackId: 'root', title: '', formatted, kind: 'whole', lineCount: lineCountOf(formatted) }];
			}

			return keys.map((k) => {
				const formatted = JSON.stringify((parsed as Record<string, unknown>)[k], null, 2);
				return {
					trackId: `k-${k}`,
					title: k,
					formatted,
					kind: 'object-key' as const,
					lineCount: lineCountOf(formatted)
				};
			});
		}

		const formatted = JSON.stringify(parsed, null, 2);
		return [{ trackId: 'root', title: '', formatted, kind: 'whole', lineCount: lineCountOf(formatted) }];
	} catch {
		return [{ trackId: 'fallback', title: '', formatted: text, kind: 'fallback', lineCount: lineCountOf(text) }];
	}
}

@Component({
	selector: 'app-edc-collapsible-json-view',
	standalone: true,
	imports: [ TranslateModule ],
	templateUrl: './edc-collapsible-json-view.component.html',
	styleUrl: './edc-collapsible-json-view.component.scss'
})

export class EdcCollapsibleJsonViewComponent {
	
	readonly jsonText = input.required<string>();
	readonly sections = computed(() => buildJsonViewSections(this.jsonText()));
	readonly viewMode = signal<'full' | 'structured'>('full');

	readonly fullDocumentText = computed((): string | null => {
		const text = (this.jsonText() ?? '').trim();
		if (!text) return null;

		try {
			return JSON.stringify(JSON.parse(text), null, 2);
		} catch {
			return text;
		}
	});

	@ViewChildren('sectionDetailsStructured')
	private structuredDetails!: QueryList<ElementRef<HTMLDetailsElement>>;

	setViewMode(mode: 'full' | 'structured'): void {
		this.viewMode.set(mode);
	}

	initialOpenStructured(index: number): boolean {
		const list = this.sections();
		return list.length === 1 || index === 0;
	}

	expandAll(): void {
		this.structuredDetails?.forEach((ref) => {
			ref.nativeElement.open = true;
		});
	}

	collapseAll(): void {
		this.structuredDetails?.forEach((ref) => {
			ref.nativeElement.open = false;
		});
	}

}